import { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Plus, X } from 'lucide-react';
import { api } from '@/api/client';
import type { VpsEntity, VpsProvider } from '@pulse/shared';
import { VPS_PROVIDER_LABELS } from '@pulse/shared';

const providers = Object.entries(VPS_PROVIDER_LABELS) as [VpsProvider, string][];

export function VpsCreate() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    name: '',
    hostname: '',
    provider: 'custom' as VpsProvider,
    ipAddress: '',
    ipv6Address: '',
    internalIp: '',
    sshPort: 22,
    sshUser: 'root',
    cpuCores: '',
    ramMb: '',
    diskGb: '',
    osName: '',
    osVersion: '',
    purpose: '',
    notes: '',
  });

  const [projects, setProjects] = useState<string[]>([]);
  const [websites, setWebsites] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [newProject, setNewProject] = useState('');
  const [newWebsite, setNewWebsite] = useState('');
  const [newTag, setNewTag] = useState('');
  const [error, setError] = useState('');

  const createMutation = useMutation({
    mutationFn: (data: unknown) => api.post<VpsEntity>('/vps', data),
    onSuccess: (vps) => {
      queryClient.invalidateQueries({ queryKey: ['vps'] });
      navigate(`/vps/${vps.id}`);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to create VPS');
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError('');

    const data = {
      ...formData,
      cpuCores: formData.cpuCores ? parseInt(formData.cpuCores, 10) : null,
      ramMb: formData.ramMb ? parseInt(formData.ramMb, 10) : null,
      diskGb: formData.diskGb ? parseInt(formData.diskGb, 10) : null,
      projects,
      websites,
      tags,
    };

    createMutation.mutate(data);
  };

  const addItem = (
    list: string[],
    setList: (items: string[]) => void,
    value: string,
    setValue: (value: string) => void
  ) => {
    const trimmed = value.trim();
    if (trimmed && !list.includes(trimmed)) {
      setList([...list, trimmed]);
    }
    setValue('');
  };

  const removeItem = (list: string[], setList: (items: string[]) => void, index: number) => {
    setList(list.filter((_, i) => i !== index));
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link
          to="/"
          className="p-2 text-gray-400 hover:text-gray-100 hover:bg-gray-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-xl font-semibold text-gray-100">Add VPS</h1>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-sm font-medium text-gray-300">Basic Information</h2>
          </div>
          <div className="card-body space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="label">
                  Name *
                </label>
                <input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input"
                  placeholder="prod-main"
                  required
                />
              </div>
              <div>
                <label htmlFor="provider" className="label">
                  Provider
                </label>
                <select
                  id="provider"
                  value={formData.provider}
                  onChange={(e) => setFormData({ ...formData, provider: e.target.value as VpsProvider })}
                  className="input"
                >
                  {providers.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="hostname" className="label">
                Hostname
              </label>
              <input
                id="hostname"
                type="text"
                value={formData.hostname}
                onChange={(e) => setFormData({ ...formData, hostname: e.target.value })}
                className="input"
                placeholder="server.example.com"
              />
            </div>
            <div>
              <label htmlFor="purpose" className="label">
                Purpose
              </label>
              <input
                id="purpose"
                type="text"
                value={formData.purpose}
                onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                className="input"
                placeholder="Production web server"
              />
            </div>
          </div>
        </div>

        {/* Network */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-sm font-medium text-gray-300">Network</h2>
          </div>
          <div className="card-body space-y-4">
            <div>
              <label htmlFor="ipAddress" className="label">
                IP Address *
              </label>
              <input
                id="ipAddress"
                type="text"
                value={formData.ipAddress}
                onChange={(e) => setFormData({ ...formData, ipAddress: e.target.value })}
                className="input font-mono"
                placeholder="192.168.1.100"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="ipv6Address" className="label">
                  IPv6 Address
                </label>
                <input
                  id="ipv6Address"
                  type="text"
                  value={formData.ipv6Address}
                  onChange={(e) => setFormData({ ...formData, ipv6Address: e.target.value })}
                  className="input font-mono"
                  placeholder="2001:db8::1"
                />
              </div>
              <div>
                <label htmlFor="internalIp" className="label">
                  Internal IP
                </label>
                <input
                  id="internalIp"
                  type="text"
                  value={formData.internalIp}
                  onChange={(e) => setFormData({ ...formData, internalIp: e.target.value })}
                  className="input font-mono"
                  placeholder="10.0.0.5"
                />
              </div>
            </div>
          </div>
        </div>

        {/* SSH */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-sm font-medium text-gray-300">SSH Connection</h2>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="sshPort" className="label">
                  SSH Port
                </label>
                <input
                  id="sshPort"
                  type="number"
                  value={formData.sshPort}
                  onChange={(e) => setFormData({ ...formData, sshPort: parseInt(e.target.value, 10) || 22 })}
                  className="input"
                  min={1}
                  max={65535}
                />
              </div>
              <div>
                <label htmlFor="sshUser" className="label">
                  SSH User
                </label>
                <input
                  id="sshUser"
                  type="text"
                  value={formData.sshUser}
                  onChange={(e) => setFormData({ ...formData, sshUser: e.target.value })}
                  className="input"
                  placeholder="root"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Specs */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-sm font-medium text-gray-300">Specifications</h2>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label htmlFor="cpuCores" className="label">
                  CPU Cores
                </label>
                <input
                  id="cpuCores"
                  type="number"
                  value={formData.cpuCores}
                  onChange={(e) => setFormData({ ...formData, cpuCores: e.target.value })}
                  className="input"
                  min={1}
                />
              </div>
              <div>
                <label htmlFor="ramMb" className="label">
                  RAM (MB)
                </label>
                <input
                  id="ramMb"
                  type="number"
                  value={formData.ramMb}
                  onChange={(e) => setFormData({ ...formData, ramMb: e.target.value })}
                  className="input"
                  min={1}
                />
              </div>
              <div>
                <label htmlFor="diskGb" className="label">
                  Disk (GB)
                </label>
                <input
                  id="diskGb"
                  type="number"
                  value={formData.diskGb}
                  onChange={(e) => setFormData({ ...formData, diskGb: e.target.value })}
                  className="input"
                  min={1}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label htmlFor="osName" className="label">
                  OS Name
                </label>
                <input
                  id="osName"
                  type="text"
                  value={formData.osName}
                  onChange={(e) => setFormData({ ...formData, osName: e.target.value })}
                  className="input"
                  placeholder="Ubuntu"
                />
              </div>
              <div>
                <label htmlFor="osVersion" className="label">
                  OS Version
                </label>
                <input
                  id="osVersion"
                  type="text"
                  value={formData.osVersion}
                  onChange={(e) => setFormData({ ...formData, osVersion: e.target.value })}
                  className="input"
                  placeholder="22.04 LTS"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Projects */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-sm font-medium text-gray-300">Projects</h2>
          </div>
          <div className="card-body">
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newProject}
                onChange={(e) => setNewProject(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addItem(projects, setProjects, newProject, setNewProject);
                  }
                }}
                className="input flex-1"
                placeholder="Add project name"
              />
              <button
                type="button"
                onClick={() => addItem(projects, setProjects, newProject, setNewProject)}
                className="btn-secondary"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {projects.map((project, i) => (
                <span key={i} className="flex items-center gap-1 px-2 py-1 bg-gray-800 rounded text-sm">
                  {project}
                  <button
                    type="button"
                    onClick={() => removeItem(projects, setProjects, i)}
                    className="text-gray-500 hover:text-gray-300"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Websites */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-sm font-medium text-gray-300">Websites</h2>
          </div>
          <div className="card-body">
            <div className="flex gap-2 mb-2">
              <input
                type="url"
                value={newWebsite}
                onChange={(e) => setNewWebsite(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addItem(websites, setWebsites, newWebsite, setNewWebsite);
                  }
                }}
                className="input flex-1"
                placeholder="https://example.com"
              />
              <button
                type="button"
                onClick={() => addItem(websites, setWebsites, newWebsite, setNewWebsite)}
                className="btn-secondary"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {websites.map((url, i) => (
                <span key={i} className="flex items-center gap-1 px-2 py-1 bg-gray-800 rounded text-sm">
                  {url}
                  <button
                    type="button"
                    onClick={() => removeItem(websites, setWebsites, i)}
                    className="text-gray-500 hover:text-gray-300"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Tags */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-sm font-medium text-gray-300">Tags</h2>
          </div>
          <div className="card-body">
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addItem(tags, setTags, newTag, setNewTag);
                  }
                }}
                className="input flex-1"
                placeholder="Add tag"
              />
              <button
                type="button"
                onClick={() => addItem(tags, setTags, newTag, setNewTag)}
                className="btn-secondary"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag, i) => (
                <span key={i} className="flex items-center gap-1 px-2 py-1 bg-pulse-500/10 text-pulse-400 rounded text-sm">
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeItem(tags, setTags, i)}
                    className="text-pulse-400/50 hover:text-pulse-400"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-sm font-medium text-gray-300">Notes</h2>
          </div>
          <div className="card-body">
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="input min-h-[100px]"
              placeholder="Additional notes about this VPS..."
            />
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Link to="/" className="btn-secondary">
            Cancel
          </Link>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="btn-primary"
          >
            {createMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Create VPS'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
